/* eslint-disable max-len */
import * as React from "react";
import { useDispatch } from "react-redux";
import { Divider, Label, Popup, Grid } from "semantic-ui-react";

import { useQuestionState } from "../../../customHooks";
import { updateQuestion, UpdateQuestionPayload } from "../../actions";
import { AnsweredQuestion, OutdatedQuestionWrapperProps, RemoveQuestionWrapperProps } from "../../model";

import { RemoveQuestionButton } from "./removeQuestion/RemoveQuestionButton";

interface QuestionWrapperProps {
    question: string | null;
    questionElement?: React.ReactNode;
    questionClassName?: string;
    inline?: boolean;
    error?: string | null;
    mandatory?: boolean;
    popupContent?: React.ReactNode;
    hideDivider?: boolean;
    extraContent?: React.ReactNode;
    noLabel?: boolean;
    readonly: boolean;
    removable: RemoveQuestionWrapperProps;
    outdated: (Partial<OutdatedQuestionWrapperProps> & { questionId: string }) | OutdatedQuestionWrapperProps; // no longer used on new forms, but still needed on completed forms
    warningMessage?: string;
    useExtendedLabel?: boolean;
}

const hasAnswerBeenGiven = (question: AnsweredQuestion): boolean => {
    const given = question.answer !== undefined;
    return given;
};

const QuestionWrapper: React.FunctionComponent<QuestionWrapperProps> =
    ({ question, questionElement, questionClassName, error, mandatory, children, popupContent, inline, hideDivider, extraContent, noLabel, removable, readonly, outdated, warningMessage, useExtendedLabel }) => {
        const dispatch = useDispatch();

        const isRemovable = removable.questionnaireKey && removable.sectionId && removable.questionId;
        const hasOutdatedSetting = outdated.questionnaireKey && outdated.sectionId && outdated.questionId;
        const isOutdatedAndReadonly = outdated.isOutdated && readonly;
        const isNotOutdatedAndReadonly = outdated.isOutdated === false && readonly;
        const removedQuestionState = useQuestionState(removable);
        const isRemovedByUser = removedQuestionState?.isRemovedByUser;
        const outdatedQuestionState = (isOutdatedAndReadonly || isNotOutdatedAndReadonly || hasOutdatedSetting) ? useQuestionState(outdated as OutdatedQuestionWrapperProps) : null; // eslint-disable-line react-hooks/rules-of-hooks

        const warning = !readonly && warningMessage && <Label color="blue" pointing>{warningMessage}</Label>;

        const setOutdatedQuestionAsValid = () => {
            if (outdatedQuestionState && !outdatedQuestionState.isValid) {
                const payload: UpdateQuestionPayload = {
                    questionnaireKey: outdated.questionnaireKey,
                    sectionId: outdated.sectionId,
                    id: outdated.questionId,
                    question: {
                        ...outdatedQuestionState,
                        isValid: true
                    }
                };
                dispatch(updateQuestion(payload));
            }
        };

        const label = React.useMemo(() => {
            if (readonly) {
                if (useExtendedLabel) {
                    return (
                        <p className={`${questionClassName} question-label`}>
                            {question}{mandatory ? "*" : ""}
                            {questionElement}
                        </p>
                    );
                }

                return (
                    <p className={`${questionClassName} question-label`}>
                        {questionElement ? questionElement : question}{mandatory ? "*" : ""}
                    </p>
                );
            }

            if (useExtendedLabel) {
                return (
                    <p className={questionClassName}>
                        {question}{mandatory ? "*" : ""}
                        {questionElement}
                    </p>
                );
            } else {
                return (
                    <p className={questionClassName}>{questionElement ? questionElement : question}{mandatory ? "*" : ""}</p>
                );
            }
        }, [readonly, useExtendedLabel, questionClassName, question, mandatory, questionElement]);

        let questionContent;
        if (isRemovedByUser) {
            return null;
        } else if (!readonly && outdated.isOutdated) { // Do not display outdated questions on new forms
            setOutdatedQuestionAsValid();
            return null;
        } else if ((isOutdatedAndReadonly || isNotOutdatedAndReadonly) && !hasAnswerBeenGiven(outdatedQuestionState)) {
            return null; // Do not display outdated questions on completed forms
        } else if (inline) {
            questionContent = (
                <Grid container className="inline-question-answer-grid">
                    {extraContent}
                    <Grid.Row className="question-answer-row">
                        <Grid.Column mobile="16" tablet="3" computer="3" className="inline-question primary-column">
                            {(isRemovable && !readonly) ?
                                <Grid columns="equal">
                                    <Grid.Column>{label}</Grid.Column>
                                    <Grid.Column width={3}><RemoveQuestionButton removable={removable} /></Grid.Column>
                                </Grid>
                                : <>{label}</>
                            }
                        </Grid.Column>
                        <Grid.Column mobile="16" tablet="13" computer="13">
                            {children}
                            {error && <Label basic color="red" pointing>{error}</Label>}
                            {warning}
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            );
        } else {
            questionContent = (
                <div className="question">
                    {extraContent}
                    {!noLabel && <>{label}</>}
                    {children}
                    {error && <Label basic color="red" pointing>{error}</Label>}
                    {warning}
                    <Divider hidden={hideDivider} />
                </div>
            );
        }

        if (popupContent) {
            return (
                <Popup trigger={questionContent} wide="very" on="focus">
                    {popupContent}
                </Popup>
            );
        } else {
            return questionContent;
        }
    };

export { QuestionWrapper };
